/**
 * WH-RESTOCK-01 (Seth 2026-09-29): the warehouse Restock list + easy cycle counts.
 *
 * Seth: "there are times when the warehouse person might need to just bring in
 * more stock materials… walking the warehouse and putting together the items
 * they need to order… Could Pegasus figure out where we typically order those
 * parts from — create POs automatically that the warehouse person would just
 * have to give our vendors?" And on counts: "We've tried to utilize cycle
 * counts but it feels like my GM forgets after a couple months."
 *
 * The investigation behind the design (SESSION-LOG 34hb): ESC's inventory
 * module was NEVER configured — 0 of 3,404 active items carry a reorder point,
 * aisle or bin, and ESC exposes no on-hand history at all. Counts lived on
 * paper and memory, which is exactly why they die after two months. So the
 * design principle here is: THE SYSTEM REMEMBERS, NOT THE GM.
 *
 * Three pieces, all riding existing machinery (never a parallel system):
 *  - pegasus_restock_line  — one running stock list. Add lines from the price
 *    book while walking the aisles; every add asks `recommend-vendor` (the same
 *    brain the Item editor uses) "where do we usually buy this?" and stamps the
 *    suggestion on the line.
 *  - Make the POs — groups open lines by vendor and drafts ONE Pegasus PO per
 *    vendor through useCreateNativePo (draft: review-before-commit, same as
 *    every other money-adjacent action). Receiving is the existing per-line
 *    receive_po; a DB trigger bumps item.qty_in_stock for count-tracked items.
 *  - pegasus_stock_count + item.count_tracked/last_counted_at — OPT-IN cycle
 *    counts ("honest counts only", the REFRIG-STOCK-COUNT lesson). The card on
 *    /warehouse always shows the few items most overdue for a count; saving
 *    writes history + updates on-hand + restamps the rotation.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Item, VendorRecommendation } from "@/features/items/items";

export interface RestockLine {
  id: string;
  item_id: string | null;
  part_number: string | null;
  description: string;
  qty: number;
  unit_cost: number | null;
  vendor_code: string | null;
  vendor_name: string | null;
  vendor_reason: string | null;
  status: "open" | "ordered" | "cancelled";
  po_id: string | null;
  /** For a job? (slice 3): the call + ticket this line is really for. */
  dispatch_no: string | null;
  dispatch_label: string | null;
  ticket_id: string | null;
  custno: string | null;
  locno: string | null;
  added_by: string | null;
  created_at: string;
}

/** Every line still on the list (not yet rolled onto a PO). */
export function useRestockLines() {
  return useQuery({
    queryKey: ["restock_lines"],
    staleTime: 15 * 1000,
    queryFn: async (): Promise<RestockLine[]> => {
      const { data, error } = await supabase.from("pegasus_restock_line")
        .select("*").eq("status", "open").order("created_at");
      if (error) throw error;
      return (data ?? []) as RestockLine[];
    },
  });
}

/**
 * Add to the list — a price-book item (with the vendor suggestion) or a
 * free-typed line ("furnace mats") that gets its vendor picked by hand.
 * Adding a part that's already on the list just bumps its quantity.
 */
export function useAddRestockLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { item: Item } | { text: string }) => {
      const { data: u } = await supabase.auth.getUser();
      const email = u?.user?.email ?? null;

      if (!("item" in v)) {
        const text = v.text.trim();
        if (!text) return;
        const { error } = await supabase.from("pegasus_restock_line")
          .insert({ description: text, qty: 1, added_by: email });
        if (error) throw error;
        return;
      }

      const it = v.item;
      // Same part already open on the list? +1 instead of a duplicate row.
      const { data: existing } = await supabase.from("pegasus_restock_line")
        .select("id, qty").eq("status", "open").eq("part_number", it.part_number)
        .limit(1).maybeSingle();
      if (existing) {
        const { error } = await supabase.from("pegasus_restock_line")
          .update({ qty: Number(existing.qty) + 1, updated_at: new Date().toISOString() })
          .eq("id", existing.id);
        if (error) throw error;
        return;
      }

      // "Where do we usually buy this?" — best-effort; a miss never blocks the
      // add (the chip just says Pick a Vendor and the warehouse taps one).
      let vendor_code: string | null = it.vendor ?? null;
      let vendor_name: string | null = null;
      let vendor_reason: string | null = null;
      let cost: number | null = it.last_purchase ?? it.average_cost ?? null;
      try {
        const { data } = await supabase.functions.invoke("recommend-vendor", { body: { part: it.part_number } });
        const rec = (data as VendorRecommendation | null)?.recommendation;
        if (rec) {
          vendor_code = rec.vendor_code ?? vendor_code;
          vendor_name = rec.vendor_name ?? null;
          vendor_reason = rec.reason ?? null;
          if (rec.price != null) cost = rec.price;
        }
      } catch { /* no suggestion — human picks */ }

      const { error } = await supabase.from("pegasus_restock_line").insert({
        item_id: it.id,
        part_number: it.part_number,
        description: it.description ?? it.part_number,
        qty: 1,
        unit_cost: cost,
        vendor_code, vendor_name, vendor_reason,
        added_by: email,
      });
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["restock_lines"] }); },
  });
}

/** Change a line in place: quantity, vendor, or drop it from the list. */
export function useUpdateRestockLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      id: string;
      qty?: number;
      /** MAT-style lines: rename to what was actually ordered (tap the name). */
      description?: string;
      status?: "cancelled";
      vendor_code?: string | null;
      vendor_name?: string | null;
      vendor_reason?: string | null;
      dispatch_no?: string | null;
      dispatch_label?: string | null;
      ticket_id?: string | null;
      custno?: string | null;
      locno?: string | null;
    }) => {
      const { id, ...rest } = v;
      const { error } = await supabase.from("pegasus_restock_line")
        .update({ ...rest, updated_at: new Date().toISOString() }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["restock_lines"] }); },
  });
}

/** After a vendor's draft PO is cut, its lines leave the list — stamped with the PO. */
export function useMarkLinesOrdered() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { ids: string[]; po_id: string }) => {
      const { data: u } = await supabase.auth.getUser();
      const email = u?.user?.email ?? null;
      const now = new Date().toISOString();
      const { error } = await supabase.from("pegasus_restock_line")
        .update({ status: "ordered", po_id: v.po_id, ordered_at: now, ordered_by: email, updated_at: now })
        .in("id", v.ids);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["restock_lines"] }); },
  });
}

// ---------------------------------------------------------------------------
// Cycle counts
// ---------------------------------------------------------------------------

export interface CountDueItem {
  id: string;
  part_number: string;
  description: string | null;
  units: string | null;
  qty_in_stock: number | null;
  last_counted_at: string | null;
}

/** The next few tracked items due a count — never-counted first, then oldest. */
export function useCountDue(limit = 5) {
  return useQuery({
    queryKey: ["count_due", limit],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<{ due: CountDueItem[]; tracked: number }> => {
      const [{ data, error }, { count, error: ce }] = await Promise.all([
        supabase.from("item")
          .select("id, part_number, description, units, qty_in_stock, last_counted_at")
          .eq("count_tracked", true).eq("inactive", false).eq("archived", false)
          .order("last_counted_at", { ascending: true, nullsFirst: true })
          .order("part_number")
          .limit(limit),
        supabase.from("item").select("id", { count: "exact", head: true })
          .eq("count_tracked", true).eq("inactive", false).eq("archived", false),
      ]);
      if (error) throw error;
      if (ce) throw ce;
      return { due: (data ?? []) as CountDueItem[], tracked: count ?? 0 };
    },
  });
}

/** Opt an item into (or out of) the count rotation. */
export function useTrackItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; tracked: boolean }) => {
      const { error } = await supabase.from("item").update({ count_tracked: v.tracked }).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["count_due"] });
      qc.invalidateQueries({ queryKey: ["items"] });
    },
  });
}

/** Save a batch of honest SHELF counts: history row + on-hand + rotation restamp. */
export function useSaveCounts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rows: { item: CountDueItem; counted: number }[]) => {
      if (rows.length === 0) return;
      const { data: u } = await supabase.auth.getUser();
      const email = u?.user?.email ?? null;
      const now = new Date().toISOString();
      const { error } = await supabase.from("pegasus_stock_count").insert(rows.map((r) => ({
        item_id: r.item.id,
        part_number: r.item.part_number,
        counted_qty: r.counted,
        previous_qty: r.item.qty_in_stock,
        source: "cycle",
        counted_by: email,
      })));
      if (error) throw error;
      for (const r of rows) {
        const { error: ie } = await supabase.from("item")
          .update({ qty_in_stock: r.counted, last_counted_at: now }).eq("id", r.item.id);
        if (ie) throw ie;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["count_due"] });
      qc.invalidateQueries({ queryKey: ["items"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Per-person (truck) counts — WH-RESTOCK-02
// ---------------------------------------------------------------------------
// The old Drive workbooks were one sheet per tech + one for the warehouse
// (all of 2026's sat empty). Pegasus keeps that shape: each person carries
// their own item list; the row itself holds the truck truth (last_qty).
// A truck count NEVER touches item.qty_in_stock — that's the shelf.

export interface CountListRow {
  id: string;
  owner_empno: string;
  owner_label: string;
  item_id: string;
  part_number: string | null;
  description: string | null;
  last_counted_at: string | null;
  last_qty: number | null;
}

/** Due = never counted, or counted longer ago than the owner's cadence. */
export const countDue = (r: CountListRow, intervalDays = 7): boolean =>
  !r.last_counted_at || Date.now() - new Date(r.last_counted_at).getTime() > intervalDays * 864e5;

export function useCountLists(owner?: string, enabled = true) {
  return useQuery({
    queryKey: ["count_lists", owner ?? "all"],
    enabled,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<CountListRow[]> => {
      let q = supabase.from("pegasus_count_list").select("*")
        .order("last_counted_at", { ascending: true, nullsFirst: true })
        .order("part_number");
      if (owner) q = q.eq("owner_empno", owner);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as CountListRow[];
    },
  });
}

/** Put an item on someone's count list (idempotent). */
export function useAddCountItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { owner_empno: string; owner_label: string; item: Item }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_count_list").upsert({
        owner_empno: v.owner_empno,
        owner_label: v.owner_label,
        item_id: v.item.id,
        part_number: v.item.part_number,
        description: v.item.description ?? v.item.part_number,
        added_by: u?.user?.email ?? null,
      }, { onConflict: "owner_empno,item_id", ignoreDuplicates: true });
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["count_lists"] }); },
  });
}

export function useRemoveCountItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("pegasus_count_list").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["count_lists"] }); },
  });
}

/** Save truck counts: history rows (stamped with WHO) + restamp each list row. */
export function useSaveTruckCounts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rows: { row: CountListRow; counted: number }[]) => {
      if (rows.length === 0) return;
      const { data: u } = await supabase.auth.getUser();
      const email = u?.user?.email ?? null;
      const now = new Date().toISOString();
      const { error } = await supabase.from("pegasus_stock_count").insert(rows.map((r) => ({
        item_id: r.row.item_id,
        part_number: r.row.part_number,
        counted_qty: r.counted,
        previous_qty: r.row.last_qty,
        source: "cycle",
        counted_by: email,
        owner_empno: r.row.owner_empno,
        owner_label: r.row.owner_label,
      })));
      if (error) throw error;
      for (const r of rows) {
        const { error: le } = await supabase.from("pegasus_count_list")
          .update({ last_qty: r.counted, last_counted_at: now }).eq("id", r.row.id);
        if (le) throw le;
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["count_lists"] }); },
  });
}

// ---------------------------------------------------------------------------
// For a job? (slice 3) — "oh i know wayne needs a part for X house"
// ---------------------------------------------------------------------------
// Pick the tech → their FLAGGED part tickets surface first (join the ticket
// the tech already filed instead of duplicating it) → else their recent calls.

export interface TechPartTicket {
  id: string;
  custno: string | null;
  locno: string | null;
  customer_name: string;
  address: string | null;
  summary: string;
  part_number: string | null;
  part_state: string | null;
  origin_dispatch: string | null;
  dispatch_no: number | null;
}

/** The tech's open "needs part" tickets (Parts Desk lane, not yet ordered). */
export function useTechOpenPartTickets(empno: string | null) {
  return useQuery({
    queryKey: ["tech_part_tickets", empno],
    enabled: !!empno,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<TechPartTicket[]> => {
      const { data, error } = await supabase.from("pegasus_dispatch")
        .select("id, custno, locno, customer_name, address, summary, part_number, part_state, origin_dispatch, dispatch_no")
        .eq("lane_key", "order_parts")
        .eq("status", "pending_schedule")
        .is("deleted_at", null)
        .in("part_state", ["needs_ordering", "backordered"])
        .eq("affinity_empno", empno as string)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return (data ?? []) as TechPartTicket[];
    },
  });
}

export interface TechRecentCall {
  dispatch: string;
  date: string | null;
  custno: string | null;
  locno: string | null;
  customer: string;
  note: string | null;
}

/** The tech's calls from the last ~2 weeks (ESC mirror), newest first. */
export function useTechRecentCalls(empno: string | null, days = 14) {
  return useQuery({
    queryKey: ["tech_recent_calls", empno, days],
    enabled: !!empno,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<TechRecentCall[]> => {
      const since = new Date(Date.now() - days * 864e5).toISOString().slice(0, 10);
      const { data: dt, error } = await supabase.from("mirror_disptech")
        .select("dispatch, dispdate")
        .eq("serviceman", empno as string)
        .gte("dispdate", since)
        .order("dispdate", { ascending: false })
        .limit(30);
      if (error) throw error;
      const seen = new Set<string>();
      const picks: { dispatch: string; date: string | null }[] = [];
      for (const r of (dt ?? []) as { dispatch: string; dispdate: string | null }[]) {
        if (r.dispatch && !seen.has(r.dispatch)) {
          seen.add(r.dispatch);
          picks.push({ dispatch: r.dispatch, date: r.dispdate });
        }
      }
      if (picks.length === 0) return [];
      const { data: ds } = await supabase.from("mirror_dispatch")
        .select("dispatch, custno, locno, notes")
        .in("dispatch", picks.map((p) => p.dispatch));
      type DRow = { dispatch: string; custno: string | null; locno: string | null; notes: string | null };
      const byDisp = new Map<string, DRow>(((ds ?? []) as DRow[]).map((d) => [d.dispatch, d]));
      const custnos = [...new Set(((ds ?? []) as DRow[]).map((d) => d.custno).filter(Boolean))] as string[];
      const names = new Map<string, string>();
      if (custnos.length) {
        const { data: cs } = await supabase.from("mirror_customer").select("custno, fullname").in("custno", custnos);
        for (const c of (cs ?? []) as { custno: string; fullname: string | null }[]) {
          if (c.fullname) names.set(c.custno, c.fullname);
        }
      }
      return picks.map((p) => {
        const d = byDisp.get(p.dispatch);
        return {
          dispatch: p.dispatch,
          date: p.date,
          custno: d?.custno ?? null,
          locno: d?.locno ?? null,
          customer: (d?.custno ? names.get(d.custno) : null) ?? d?.custno ?? `Call ${p.dispatch}`,
          note: d?.notes ? String(d.notes).replace(/\s+/g, " ").slice(0, 80) : null,
        };
      });
    },
  });
}

/** Bulk-seed count lists (multi-person add / copy a list). Idempotent. */
export function useSeedCountRows() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rows: { owner_empno: string; owner_label: string; item_id: string; part_number: string | null; description: string | null }[]) => {
      if (rows.length === 0) return;
      const { data: u } = await supabase.auth.getUser();
      const email = u?.user?.email ?? null;
      const { error } = await supabase.from("pegasus_count_list")
        .upsert(rows.map((r) => ({ ...r, added_by: email })), { onConflict: "owner_empno,item_id", ignoreDuplicates: true });
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["count_lists"] }); },
  });
}

// ---------------------------------------------------------------------------
// Count owners — durable setup (people + cadence persist; nothing to redo
// next season). History (pegasus_stock_count) keeps every count forever.
// ---------------------------------------------------------------------------

export interface CountOwner {
  owner_empno: string;
  owner_label: string;
  interval_days: number;
  active: boolean;
}

export function useCountOwners() {
  return useQuery({
    queryKey: ["count_owners"],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<CountOwner[]> => {
      const { data, error } = await supabase.from("pegasus_count_owner")
        .select("owner_empno, owner_label, interval_days, active")
        .order("owner_label");
      if (error) throw error;
      return (data ?? []) as CountOwner[];
    },
  });
}

/** Add people (idempotent — re-adding never resets someone's cadence). */
export function useAddOwners() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (people: { empno: string; label: string }[]) => {
      if (people.length === 0) return;
      const { data: u } = await supabase.auth.getUser();
      const email = u?.user?.email ?? null;
      const { error } = await supabase.from("pegasus_count_owner")
        .upsert(people.map((p) => ({ owner_empno: p.empno, owner_label: p.label, added_by: email })),
          { onConflict: "owner_empno", ignoreDuplicates: true });
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["count_owners"] }); },
  });
}

export function useUpdateOwner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { empno: string; interval_days?: number; active?: boolean }) => {
      const { empno, ...rest } = v;
      const { error } = await supabase.from("pegasus_count_owner").update(rest).eq("owner_empno", empno);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["count_owners"] }); },
  });
}

/** Take a person off counts: their list goes, their count HISTORY stays. */
export function useRemoveOwner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (empno: string) => {
      const { error: le } = await supabase.from("pegasus_count_list").delete().eq("owner_empno", empno);
      if (le) throw le;
      const { error } = await supabase.from("pegasus_count_owner").delete().eq("owner_empno", empno);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["count_owners"] });
      qc.invalidateQueries({ queryKey: ["count_lists"] });
    },
  });
}

// ---------------------------------------------------------------------------
// The Pegasus roster for the people picker — tech_profile (the Team page's
// truth), NOT ESC's employee table: no dummy accounts (Unassigned, Return
// Installs, Reschedule…), clean group names, Sales = the actual salesperson.
// ---------------------------------------------------------------------------

export interface CountRosterPerson {
  empno: string;
  name: string;
  group: string;
}

export function useCountRoster() {
  return useQuery({
    queryKey: ["count_roster"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<CountRosterPerson[]> => {
      const [{ data: profiles, error }, { data: emps }] = await Promise.all([
        supabase.from("tech_profile").select("empno, display_name, roster_group").is("end_date", null),
        supabase.from("mirror_employee").select("empno, empname, firstname, lastname"),
      ]);
      if (error) throw error;
      const nameBy = new Map<string, string>();
      for (const e of (emps ?? []) as { empno: string; empname: string | null; firstname: string | null; lastname: string | null }[]) {
        const n = e.empname?.trim() || `${e.firstname ?? ""} ${e.lastname ?? ""}`.trim();
        if (n) nameBy.set(e.empno, n);
      }
      return ((profiles ?? []) as { empno: string; display_name: string | null; roster_group: string | null }[])
        .map((p) => ({
          empno: p.empno,
          name: p.display_name?.trim() || nameBy.get(p.empno) || p.empno,
          group: p.roster_group?.trim() || "Unassigned",
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  });
}
